import { lazy, memo, Suspense, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type PublicClip } from '@air/api';
import { type Clip, ClipStatus } from '@air/api/types';
import { NetworkStatusInfo } from '@air/classes-network-status-info';
import { ClipUtils, clearThumbnailCache } from '@air/utils-clips';
import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';

import { AssetGalleryCardThumbnailImage } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardThumbnailImage';
import { SelectedGalleryCardOverlay } from '~/components/Gallery/GalleryCard/SelectedGalleryCardOverlay';
import { CDN_IMAGE_LOAD_TIMEOUT_MS } from '~/constants/assetCard';
import { getImagePreviewFallbackUrl } from '~/constants/cloudfront';
import { getPreferenceClassName, getPreferenceImgixFit, useThumbnailPreference } from '~/hooks/useThumbnailPreference';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import { getCanvasImportPreview, releaseCanvasImportPreview } from '~/utils/canvasImportPreviewCache';
import { getTransparentPreviewSource, preferredPreviewSrc } from '~/utils/getTransparentPreviewSource';
import { releaseUploadDimensions } from '~/utils/uploadDimensionsCache';
import { getUploadPreview, releaseUploadPreview } from '~/utils/uploadPreviewCache';

const ClipPreview = lazy(async () => {
  try {
    const loadedModule = await import('~/components/Shared/ClipList/ClipItem/ClipPreview');

    return { default: loadedModule.ClipPreview };
  } catch (error) {
    if (NetworkStatusInfo.isOnline) {
      reportErrorToBugsnag({
        error: convertUnknownToError(error),
        context: 'Failed to lazy load ClipPreview',
      });
    }

    return Promise.resolve({ default: () => <div /> });
  }
});

export type AssetGalleryCardThumbnailProps = {
  clip: Clip | PublicClip;
  isHovering: boolean;
  isSelected: boolean;
  onPreviewReleased?: () => void;
  width: number;
  height: number;
};

const _AssetGalleryCardThumbnail = ({
  clip,
  isHovering,
  isSelected,
  onPreviewReleased,
  width,
  height,
}: AssetGalleryCardThumbnailProps) => {
  const videoSrc = clip.assets.previewVideo;
  const playbackRate = ClipUtils.determinePlaybackRate(clip.duration ?? 0, videoSrc ?? '');
  const vertical = clip.status === ClipStatus.transcoded && !ClipUtils.isLandscape(clip.rotation);
  const { getPreference } = useThumbnailPreference();
  const boardId = useSelector(CentralizedBoardSelectors.boardId);

  const thumbnailPreference = useMemo(() => getPreference(boardId), [boardId, getPreference]);

  // Don't fall back to clip.assets.image during processing — the CDN URL may be set
  // before it's accessible, causing an invisible image (opacity-0, never loads)
  const transparentPreview = useMemo(
    () =>
      getTransparentPreviewSource(clip.ext, {
        image: clip.assets.image,
        original: clip.assets.original,
      }),
    [clip.assets.image, clip.assets.original, clip.ext],
  );
  const isWaitingForProcessedPreview = ClipUtils.isProcessing(clip.status) && !clip.assets.spriteSheet?.src;
  const localPreviewSrc = getCanvasImportPreview(clip.importedName) ?? getUploadPreview(clip.importedName) ?? '';
  const transcodedSrc = preferredPreviewSrc(transparentPreview, clip.assets.image);
  const imageSrc = isWaitingForProcessedPreview ? localPreviewSrc : transcodedSrc;

  useEffect(() => {
    if (!clip.importedName || ClipUtils.isProcessing(clip.status)) {
      return;
    }

    releaseCanvasImportPreview(clip.importedName);
    releaseUploadPreview(clip.importedName);
    releaseUploadDimensions(clip.importedName);

    // For failed/nonTranscodable clips no thumbnail image will ever load,
    // so clear the canvas import preview state immediately.
    // For transcoded clips, onPreviewReleased is called via onFinalImageLoad
    // once the CDN thumbnail actually loads (keeping the shimmer visible until then),
    // but fall back after 5s in case the CDN image never loads (network error, CDN 404).
    if (ClipUtils.isFailed(clip.status) || ClipUtils.isNonTranscodable(clip.status)) {
      onPreviewReleased?.();
    } else {
      const timeout = setTimeout(() => onPreviewReleased?.(), CDN_IMAGE_LOAD_TIMEOUT_MS);

      return () => clearTimeout(timeout);
    }
  }, [clip.importedName, clip.status, onPreviewReleased]);

  const CardThumbnail = useMemo(
    () => (
      <AssetGalleryCardThumbnailImage
        {...clip}
        className={getPreferenceClassName(thumbnailPreference)}
        animatedSrc={clip.type === 'animated' && isHovering && videoSrc ? videoSrc : ''}
        fallbackUrl={
          transparentPreview.preserveTransparency && transparentPreview.fallbackSrc
            ? getImagePreviewFallbackUrl(transparentPreview.fallbackSrc) || undefined
            : undefined
        }
        imgixParams={{
          fit: getPreferenceImgixFit(thumbnailPreference),
        }}
        preserveTransparency={transparentPreview.preserveTransparency && imageSrc === transparentPreview.src}
        processedPreviewSrc={
          transparentPreview.preserveTransparency && imageSrc === transparentPreview.src
            ? transparentPreview.fallbackSrc
            : undefined
        }
        src={clearThumbnailCache({
          clipExt: clip.ext,
          clipStatus: clip.status,
          imageSrc,
          clipUpdatedAt: clip.updatedAt,
        })}
        imageHeight={clip.height}
        imageWidth={clip.width}
        width={width}
        height={height}
        onFinalImageLoad={onPreviewReleased}
      />
    ),
    [clip, imageSrc, isHovering, onPreviewReleased, thumbnailPreference, transparentPreview, videoSrc, width, height],
  );

  return (
    <>
      <div className="absolute inset-0 flex overflow-hidden rounded-4">
        {CardThumbnail}
        {isSelected && <SelectedGalleryCardOverlay />}
      </div>
      {!!videoSrc && clip.type === 'video' && !ClipUtils.isProcessing(clip.status) && isHovering && (
        <Suspense fallback={CardThumbnail}>
          <ClipPreview vertical={vertical} playbackRate={playbackRate} status={clip.status} src={videoSrc} />
        </Suspense>
      )}
    </>
  );
};

export const AssetGalleryCardThumbnail = memo(_AssetGalleryCardThumbnail);
AssetGalleryCardThumbnail.displayName = 'AssetGalleryCardThumbnail';
